import { createSlice } from '@reduxjs/toolkit';

/**
 * Lifecycle of the AI legal-disclaimer consent check for the current
 * session. Modelled `as const` (repo convention over `enum`).
 *
 * - `idle` — no check has run yet, or the user declined and the gate
 *   was reset so the next entry re-checks.
 * - `checking` — a `getConsentStatus` lookup is in flight (triggered
 *   at Sidekick entry).
 * - `granted` — the user has accepted; the chat is unblocked.
 * - `denied` — the user has not accepted; the in-drawer consent modal
 *   is shown and blocks the chat.
 * - `submitting` — the user clicked Agree and `acceptConsent` is in
 *   flight; the modal stays mounted with the Agree button loading.
 * - `error` — the status lookup rejected; the error toast is shown and
 *   the panel is not opened.
 */
export const CONSENT_STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  GRANTED: 'granted',
  DENIED: 'denied',
  SUBMITTING: 'submitting',
  ERROR: 'error',
} as const;

export type ConsentStatus =
  (typeof CONSENT_STATUS)[keyof typeof CONSENT_STATUS];

/** Redux state for the Sidekick consent gate. */
export type AISidekickConsentState = {
  /** Current phase of the consent check for this session. */
  status: ConsentStatus;
};

/**
 * Minimum shape the consumer's `RootState` must satisfy for the
 * consent selectors. Kept as its own structural type (like
 * `StateWithSidekickChat`) so surfaces can mount the consent reducer
 * independently of the UI / chat slices.
 */
export type StateWithSidekickConsent = {
  aiSidekickConsent: AISidekickConsentState;
};

const initialState: AISidekickConsentState = {
  status: CONSENT_STATUS.IDLE,
};

/**
 * Redux slice for the AI legal-disclaimer consent gate. Shared between
 * the entry card (which triggers the check on open-intent) and the
 * panel body (which renders the in-drawer consent modal when the
 * status is `denied`). The orchestration lives in
 * `useSidekickConsentGate`; this slice only holds the resolved status.
 */
export const aiSidekickConsentSlice = createSlice({
  name: 'aiSidekickConsent',
  initialState,
  reducers: {
    /** Marks a `getConsentStatus` lookup as in flight. */
    consentChecking: (state) => {
      state.status = CONSENT_STATUS.CHECKING;
    },
    /**
     * Records that the user has accepted the disclaimer, whether the
     * answer came from the backend or from the browser cache.
     */
    consentGranted: (state) => {
      state.status = CONSENT_STATUS.GRANTED;
    },
    /** Records that the user has not accepted — show the consent modal. */
    consentDenied: (state) => {
      state.status = CONSENT_STATUS.DENIED;
    },
    /** Marks an `acceptConsent` submission as in flight (Agree clicked). */
    consentSubmitting: (state) => {
      state.status = CONSENT_STATUS.SUBMITTING;
    },
    /** Records that the status lookup failed — the error toast is shown. */
    consentError: (state) => {
      state.status = CONSENT_STATUS.ERROR;
    },
    /**
     * Resets the gate to `idle` so the next Sidekick entry re-checks.
     * Dispatched when the user declines (Disagree / X) and the panel is
     * collapsed, so the modal re-appears on every re-entry until the
     * user consents.
     */
    consentReset: (state) => {
      state.status = CONSENT_STATUS.IDLE;
    },
  },
});

export const {
  consentChecking,
  consentGranted,
  consentDenied,
  consentSubmitting,
  consentError,
  consentReset,
} = aiSidekickConsentSlice.actions;

/**
 * Selects the current consent-check status. Falls back to `idle` when
 * the consent reducer is not mounted, so surfaces that compose only a
 * subset of the Sidekick slices (or tests with a partial store) read a
 * safe default instead of throwing — mirroring the defensive slice
 * reads elsewhere in the panel.
 */
export const selectConsentStatus = (
  state: Partial<StateWithSidekickConsent>
): ConsentStatus => state.aiSidekickConsent?.status ?? CONSENT_STATUS.IDLE;

/** Selects whether the user has accepted the disclaimer (chat unblocked). */
export const selectIsConsentGranted = (
  state: Partial<StateWithSidekickConsent>
): boolean => selectConsentStatus(state) === CONSENT_STATUS.GRANTED;

/**
 * Selects whether the in-drawer consent modal should be mounted. True
 * while the user has not consented (`denied`) and while their Agree
 * click is in flight (`submitting`) so the modal stays put with its
 * loading affordance.
 */
export const selectIsConsentGateOpen = (
  state: Partial<StateWithSidekickConsent>
): boolean => {
  const status = selectConsentStatus(state);
  return (
    status === CONSENT_STATUS.DENIED || status === CONSENT_STATUS.SUBMITTING
  );
};

export default aiSidekickConsentSlice.reducer;
